import React from 'react';
import kebabCase from 'lodash/kebabCase';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import StatBarStat, { IStatBarStatProps } from '../Stat';

export interface IStatsBarProps extends IBaseElementProps {
	/**
	 * Theme - Default: Dark
	 */
	theme?: 'Light' | 'Dark';
	/**
	 * Layout - Default: Horizontal
	 */
	layout?: 'Simple' | 'Two Column' | 'Extended';
	/**
	 * Heading
	 */
	heading?: string;
	/**
	 * Description
	 * @control richtext
	 */
	description?: string;
	/**
	 * Stats
	 */
	stats: IStatBarStatProps[];
	/**
	 * This will only invert if the theme is set to dark.
	 */
	inverted?: boolean;
	turnOffCountUp?: boolean;
}

/**
 * Calian Stats Bar
 * @icon analytics
 * @block
 */
const StatsBar: React.FC<IStatsBarProps> = ({
	id,
	className,
	classNames = [],
	theme,
	layout,
	heading,
	description,
	stats,
	inverted = false,
	turnOffCountUp = false,
}) => {
	let content = null;

	switch (layout) {
		case 'Simple':
			content = (
				<div
					className={cn('container-fluid', {
						[`layout-${kebabCase(layout)}`]: layout,
					})}
				>
					<div className="row mw-1830px mx-auto">
						{stats?.map((stat, index) => (
							<div
								className={`col-12 col-sm${
									stats.length >= 4 ? '-6' : ''
								} col-lg`}
								key={index}
							>
								<StatBarStat
									{...stat}
									countUp={!turnOffCountUp}
								/>
							</div>
						))}
					</div>
				</div>
			);
			break;
		case 'Two Column':
			content = (
				<div
					className={cn('container-fluid mw-1206px', {
						[`layout-${kebabCase(layout)}`]: layout,
					})}
				>
					<div className="row">
						<div className="col-12 col-lg-6 pe-lg-8">
							{heading && (
								<h2 className="calian-stats-bar__heading mt-lg-9 mb-lg-5 pt-lg-5">
									{heading}
								</h2>
							)}
							{description && (
								<p
									className="calian-stats-bar__description mb-3 fs-lg-18px ls-0 lh-base"
									dangerouslySetInnerHTML={{
										__html: description,
									}}
								/>
							)}
						</div>
						<div className="col-12 col-lg-6 rounded-lg-5 p-6 p-lg-12 bg-theme">
							<div className="d-flex flex-column">
								{stats?.map((stat, index) => (
									<StatBarStat
										key={index}
										{...stat}
										countUp={!turnOffCountUp}
									/>
								))}
							</div>
						</div>
					</div>
				</div>
			);
			break;
		case 'Extended':
			content = (
				<div
					className={cn('container-fluid px-0', {
						[`layout-${kebabCase(layout)}`]: layout,
					})}
				>
					<div className="bg-theme">
						<div className="row mw-1440px py-9 px-3 py-md-3 ps-xl-11 pe-xl-9 mx-auto">
							<div className="col-12 col-lg-5">
								{heading && (
									<h2 className="calian-stats-bar__heading mt-lg-9 mb-lg-3 pt-lg-5">
										{heading}
									</h2>
								)}
								{description && (
									<p
										className="calian-stats-bar__description pb-lg-5 mb-3 mb-lg-9 fs-lg-18px ls-0 lh-base"
										dangerouslySetInnerHTML={{
											__html: description,
										}}
									/>
								)}
							</div>
							<div className="col-12 col-lg-7 ps-lg-8">
								<div className="grid p-3 p-md-6 my-lg-9 px-lg-0 py-lg-4">
									{stats?.map((stat, index) => (
										<StatBarStat
											className={`stat d-flex flex-column text-center lh-base g-col-6 g-col-lg-${
												stats.length > 4 ? 4 : 6
											}`}
											key={index}
											{...stat}
											countUp={!turnOffCountUp}
										/>
									))}
								</div>
							</div>
						</div>
					</div>
				</div>
			);
			break;
	}

	return (
		<div
			id={id}
			className={
				className ||
				cn(
					'calian-stats-bar',
					inverted && 'calian-stats-bar--inverted',
					{
						[`theme-${kebabCase(theme)}`]: theme,
					},
					...classNames,
				)
			}
		>
			{content}
		</div>
	);
};

export default StatsBar;
